import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import {
  formatCurrency,
  formatPercent,
  getPerformanceClass,
} from "@/utils/formatters";
import { formatDate, formatTimestamp } from "@/utils/timezone";
import LaggardWidget from "./LaggardWidget";
import PortfolioGraphWidget from "./PortfolioGraphWidget";
import CashByAccountModal from "./modals/CashByAccountModal";

// Top gainer/loser tickers for a single period (Today/This Week/This Month),
// rendered under that period's P&L. Shared by all three periods to avoid
// repeating the same markup three times.
const PeriodMovers = ({ gainers, losers, accountId }) => {
  if ((!gainers || gainers.length === 0) && (!losers || losers.length === 0)) {
    return null;
  }

  const tickerLink = (ticker) =>
    accountId ? `/ticker/${ticker}?accountId=${accountId}` : `/ticker/${ticker}`;

  return (
    <div className="mt-2 space-y-0.5">
      {(gainers || []).map((g) => (
        <div key={`gain-${g.ticker}`} className="flex items-center justify-between text-xs">
          <Link
            to={tickerLink(g.ticker)}
            className="text-green-600 dark:text-green-400 hover:underline font-medium"
          >
            {g.ticker}
          </Link>
          <span className="text-green-600 dark:text-green-400">
            +{formatCurrency(g.period_pnl)}
          </span>
        </div>
      ))}
      {(losers || []).map((l) => (
        <div key={`loss-${l.ticker}`} className="flex items-center justify-between text-xs">
          <Link
            to={tickerLink(l.ticker)}
            className="text-red-600 dark:text-red-400 hover:underline font-medium"
          >
            {l.ticker}
          </Link>
          <span className="text-red-600 dark:text-red-400">
            {formatCurrency(l.period_pnl)}
          </span>
        </div>
      ))}
    </div>
  );
};

const SummaryTab = ({ accountId, refreshTrigger = 0 }) => {
  const [performance, setPerformance] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [account, setAccount] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [cashBalance, setCashBalance] = useState(0);
  const [cashByAccount, setCashByAccount] = useState([]);
  const [showCashModal, setShowCashModal] = useState(false);
  const [totalAccountValue, setTotalAccountValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [laggards, setLaggards] = useState(null);
  const [laggardsLoading, setLaggardsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch holdings data
        let holdingsResponse;
        if (accountId === null) {
          holdingsResponse = await api.get("/accounts/all/current");
        } else {
          holdingsResponse = await api.get(`/accounts/${accountId}/current`);
        }

        setHoldings(holdingsResponse.data.holdings || []);
        setCashBalance(holdingsResponse.data.cash_balance || 0);
        setCashByAccount(holdingsResponse.data.cash_by_account || []);
        setTotalAccountValue(holdingsResponse.data.total_value || 0);

        // Single-account fetches use /accounts/{id}/*, all-accounts use /accounts/all/*
        const perfUrl = accountId
          ? `/accounts/${accountId}/current-performance`
          : `/accounts/all/current-performance`;
        const txUrl = accountId
          ? `/accounts/${accountId}/transactions?limit=10&page=1`
          : `/accounts/all/transactions?limit=10&page=1`;
        const laggardsUrl = accountId
          ? `/accounts/${accountId}/laggards`
          : `/accounts/all/laggards`;

        const perfResponse = await api.get(perfUrl);
        setPerformance(perfResponse.data);

        if (accountId) {
          // Account-specific details only exist for a single account.
          const accountResponse = await api.get(`/accounts/${accountId}`);
          setAccount(accountResponse.data);
        } else {
          setAccount(null);
        }

        const txResponse = await api.get(txUrl);
        setRecentTransactions(txResponse.data.transactions || []);

        // Fetch laggards analysis
        setLaggardsLoading(true);
        try {
          const laggardsResponse = await api.get(laggardsUrl);
          setLaggards(laggardsResponse.data.laggards || []);
        } catch (error) {
          console.error("Error fetching laggards:", error);
          setLaggards([]);
        } finally {
          setLaggardsLoading(false);
        }
      } catch (error) {
        console.error("Error fetching summary data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accountId, refreshTrigger]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Calculate portfolio value (total - cash)
  const portfolioValue = totalAccountValue - cashBalance;

  // Get top gainers and losers
  const sortedHoldings = [...(holdings || [])]
    .filter((h) => h.unrealized_gain_loss != null)
    .sort(
      (a, b) => (b.unrealized_gain_loss || 0) - (a.unrealized_gain_loss || 0),
    );

  const topGainers = sortedHoldings
    .filter((h) => h.unrealized_gain_loss > 0)
    .slice(0, 3);
  const topLosers = sortedHoldings
    .filter((h) => h.unrealized_gain_loss < 0)
    .slice(-3)
    .reverse();

  return (
    <div className="space-y-6">
      {/* Account Value Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            Total Value
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(totalAccountValue)}
          </div>
        </div>

        <div
          className={`bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6 ${
            !accountId
              ? "cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
              : ""
          }`}
          onClick={!accountId ? () => setShowCashModal(true) : undefined}
        >
          <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1">
            Cash Balance
            {!accountId && (
              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(cashBalance)}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            Portfolio Value
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(portfolioValue)}
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      {performance && (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Performance
          </h3>

          {!performance.daily?.has_snapshots &&
          !performance.weekly?.has_snapshots &&
          !performance.monthly?.has_snapshots ? (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                Performance tracking requires account snapshots
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                Snapshots are created automatically each day. Check back
                tomorrow!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Today */}
              {performance.daily && (
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Today
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span
                      className={`text-2xl font-bold ${getPerformanceClass(
                        performance.daily.total_pnl,
                      )}`}
                    >
                      {formatCurrency(performance.daily.total_pnl)}
                    </span>
                    <span
                      className={`text-lg ${getPerformanceClass(
                        performance.daily.pnl_percent,
                      )}`}
                    >
                      ({formatPercent(performance.daily.pnl_percent)})
                    </span>
                  </div>
                  {!performance.daily.has_snapshots && (
                    <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      No data available
                    </div>
                  )}
                  <PeriodMovers
                    gainers={performance.daily.top_gainers}
                    losers={performance.daily.top_losers}
                    accountId={accountId}
                  />
                </div>
              )}

              {/* This Week */}
              {performance.weekly && (
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    This Week
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span
                      className={`text-2xl font-bold ${getPerformanceClass(
                        performance.weekly.total_pnl,
                      )}`}
                    >
                      {formatCurrency(performance.weekly.total_pnl)}
                    </span>
                    <span
                      className={`text-lg ${getPerformanceClass(
                        performance.weekly.pnl_percent,
                      )}`}
                    >
                      ({formatPercent(performance.weekly.pnl_percent)})
                    </span>
                  </div>
                  {!performance.weekly.has_snapshots && (
                    <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      No data available
                    </div>
                  )}
                  <PeriodMovers
                    gainers={performance.weekly.top_gainers}
                    losers={performance.weekly.top_losers}
                    accountId={accountId}
                  />
                </div>
              )}

              {/* This Month */}
              {performance.monthly && (
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {performance.monthly.start_snapshot_date
                      ? (() => {
                          const snap = new Date(performance.monthly.start_snapshot_date + "T00:00:00");
                          const thirtyDaysAgo = new Date();
                          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                          // If actual start is more than 3 days after the expected 30-day-ago date,
                          // show "since [date]" to clarify the actual measurement window.
                          return snap > thirtyDaysAgo
                            ? `Since ${snap.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                            : "This Month";
                        })()
                      : "This Month"}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span
                      className={`text-2xl font-bold ${getPerformanceClass(
                        performance.monthly.total_pnl,
                      )}`}
                    >
                      {formatCurrency(performance.monthly.total_pnl)}
                    </span>
                    <span
                      className={`text-lg ${getPerformanceClass(
                        performance.monthly.pnl_percent,
                      )}`}
                    >
                      ({formatPercent(performance.monthly.pnl_percent)})
                    </span>
                  </div>
                  {!performance.monthly.has_snapshots && (
                    <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      No data available
                    </div>
                  )}
                  <PeriodMovers
                    gainers={performance.monthly.top_gainers}
                    losers={performance.monthly.top_losers}
                    accountId={accountId}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Portfolio Graph */}
      <PortfolioGraphWidget accountId={accountId} refreshTrigger={refreshTrigger} perfData={performance} />

      {/* Top Holdings and Laggards - 3-column layout */}
      {holdings && holdings.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Top Gainers */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Top Gainers
            </h3>
            {topGainers.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No gainers
              </p>
            ) : (
              <div className="space-y-3">
                {topGainers.map((holding) => (
                  <div
                    key={holding.id}
                    className="flex items-center justify-between"
                  >
                    <div>
                      <Link
                        to={accountId ? `/ticker/${holding.ticker}?accountId=${accountId}` : `/ticker/${holding.ticker}`}
                        className="font-semibold text-gray-900 dark:text-white hover:underline cursor-pointer"
                      >
                        {holding.ticker}
                      </Link>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {holding.shares} shares
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-green-600 dark:text-green-400">
                        {formatCurrency(holding.unrealized_gain_loss)}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatCurrency(holding.market_value)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top Losers */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Top Losers
            </h3>
            {topLosers.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No losers
              </p>
            ) : (
              <div className="space-y-3">
                {topLosers.map((holding) => (
                  <div
                    key={holding.id}
                    className="flex items-center justify-between"
                  >
                    <div>
                      <Link
                        to={accountId ? `/ticker/${holding.ticker}?accountId=${accountId}` : `/ticker/${holding.ticker}`}
                        className="font-semibold text-gray-900 dark:text-white hover:underline cursor-pointer"
                      >
                        {holding.ticker}
                      </Link>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {holding.shares} shares
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-red-600 dark:text-red-400">
                        {formatCurrency(holding.unrealized_gain_loss)}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatCurrency(holding.market_value)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Momentum Laggards */}
          <LaggardWidget
            accountId={accountId}
            laggards={laggards}
            loading={laggardsLoading}
          />
        </div>
      )}

      {/* Recent Transactions */}
      {recentTransactions.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Recent Activity
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ticker
                  </th>
                  {!accountId && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Account
                    </th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Shares
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    P&L
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {recentTransactions.map((tx) => (
                  <tr
                    key={tx.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatDate(tx.date, "M/d/yy")}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          tx.action === "BUY"
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                            : tx.action === "SELL"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : tx.action === "DIVIDEND"
                                ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                        }`}
                      >
                        {tx.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {tx.ticker ? (
                        <Link
                          to={accountId ? `/ticker/${tx.ticker}?accountId=${accountId}` : `/ticker/${tx.ticker}`}
                          className="hover:underline cursor-pointer"
                        >
                          {tx.ticker}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                    {!accountId && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {tx.account_name || "-"}
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                      {tx.shares ? tx.shares : "-"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                      {tx.price ? formatCurrency(tx.price) : "-"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-white">
                      {tx.amount > 0 ? "+" : ""}
                      {formatCurrency(tx.amount)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium">
                      {tx.action === "SELL" &&
                      tx.realized_pnl !== null &&
                      tx.realized_pnl !== undefined &&
                      tx.realized_pnl !== "" ? (
                        <span
                          className={
                            parseFloat(tx.realized_pnl) > 0
                              ? "text-green-600 dark:text-green-400"
                              : parseFloat(tx.realized_pnl) < 0
                                ? "text-red-600 dark:text-red-400"
                                : "text-gray-900 dark:text-white"
                          }
                        >
                          {parseFloat(tx.realized_pnl) > 0 ? "+" : ""}
                          {formatCurrency(tx.realized_pnl)}
                        </span>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">
                          -
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Account Info */}
      {account && (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Account Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Account Type
              </div>
              <div className="font-medium text-gray-900 dark:text-white capitalize">
                {account.account_type || "N/A"}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Tax Status
              </div>
              <div className="font-medium text-gray-900 dark:text-white">
                {account.tax_status || "N/A"}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Source
              </div>
              <div className="font-medium text-gray-900 dark:text-white">
                {account.source || "MANUAL"}
              </div>
            </div>
            {account.plaid_last_synced_at && (
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Last Synced
                </div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {formatTimestamp(
                    account.plaid_last_synced_at,
                    "MMM d, h:mm a",
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showCashModal && (
        <CashByAccountModal
          accounts={cashByAccount}
          total={cashBalance}
          onClose={() => setShowCashModal(false)}
        />
      )}
    </div>
  );
};

export default SummaryTab;
